import { toast } from 'react-toastify';
import React from 'react';
import moment from 'moment';
import momentTz from 'moment-timezone';
import { createNumberMask, createTextMask } from 'redux-form-input-masks';
import Toaster from '../components/atoms/Toaster'
import { COUNTRIES_WITH_STATES } from '../helpers/constants';
import ReactGA from "react-ga";
import lodash from "lodash";
import URLSearchParams from "@ungap/url-search-params";
import { FinancialSettingsMapping, TL_SHIPMENT_MODE } from '../helpers/enums';
import {INPUT_TYPE_FLOAT, INPUT_TYPE_NUMBER, INPUT_TYPE_TEXT_WITHOUT_SP_SYMBOLS, LTL_QUOTE_FORM, TL_QUOTE_FORM } from "./enums";
import { change as reduxFormChange } from 'redux-form';
import { CRATE_ID, BOX_ID } from '../components/organisms/QuoteForm/ItemsSection/enums';
import {authHeader} from "../helpers/authHeader";
import axios from "axios";

export const ZIP_WAIT_INTERVAL = 50;

export const Name_Regex = /^[ A-Za-z]*$/
export const AlphaNumeric_Regex = /^[A-Za-z0-9]*$/
export const Number_Regex = /^[0-9]*$/
export const Float_Regex = /^[0-9.]*$/
export const AlphaNumericWhiteSpace_Regex = /^[a-zA-Z0-9 ]+$/
export const characterWithHyphen = /^[A-Za-z0-9-]*$/
export const Email_Regex = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i
export const NegativeRegex = /^-?\d+(\.\d{1,2})?$/
export const PositiveRegex = /^\d+(\.\d{1,2})?$/
export const Password_Regex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[ !"#$%&'()*+,-./\\:;<=>?@[\]^_{|}~`])[A-Za-z\d !"#$%&'()*+,-./\\:;<=>?@[\]^_{|}~`]{8,}$/i
export const File_Regex = /([a-zA-Z0-9\s_\\.\-:()])+(.pdf)$/
export const IP_Regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/

export const DATETIME_FORMAT = 'MM/DD/YYYY hh:mm:ss A';
export const WIDGET_DATETIME_FORMAT = 'MM/DD/YY [at] hh:mm A';
export const DATE_FORMAT = 'MM/DD/YYYY';
export const SYSTEM_DATE_FORMAT = 'YYYY-MM-DD';
export const TIME_FORMAT = 'hh:mm:ss A';

const BUSINESS_OPEN_HOUR = 8;
const BUSINESS_CLOSE_HOUR = 17;
const MINIMUM_PICKUP_WINDOW_HOURS = 4;
const SAME_DAY_PICKUPS_ENABLED = 1;

const getNextBusinessDay = currentDate => {
    const pickupDate = currentDate.clone().startOf('day').add(1, 'day');

    while ([0, 6].includes(pickupDate.day())) {
        pickupDate.add(1, 'day');
    }

    return pickupDate;
};

export const isSameDayPickupAvailable = ({allowSameDayPickups, timezone, now = new Date()}) => {
  if (Number(allowSameDayPickups) !== SAME_DAY_PICKUPS_ENABLED || !timezone || !momentTz.tz.zone(timezone)) {
        return false;
    }

    const customerNow = momentTz.tz(now, timezone);

    if ([0, 6].includes(customerNow.day())) {
        return false;
    }

    const earliestPickupTime = momentTz.max(
        customerNow,
        customerNow.clone().startOf('day').hour(BUSINESS_OPEN_HOUR)
    );
    const businessCloseTime = customerNow.clone().startOf('day').hour(BUSINESS_CLOSE_HOUR);

    return earliestPickupTime.clone()
        .add(MINIMUM_PICKUP_WINDOW_HOURS, 'hours')
        .isSameOrBefore(businessCloseTime);
};

export const getEarliestSameDayPickupWindow = ({timezone, now = new Date()}) => {
    if (!timezone || !momentTz.tz.zone(timezone)) return null;

    const originNow = momentTz.tz(now, timezone);
    const start = originNow.clone().startOf('hour');
    if (start.isBefore(originNow)) start.add(1, 'hour');
    if (start.hour() < BUSINESS_OPEN_HOUR) start.hour(BUSINESS_OPEN_HOUR);

    const end = start.clone().add(MINIMUM_PICKUP_WINDOW_HOURS, 'hours');
    if ([0, 6].includes(start.day()) || end.hour() > BUSINESS_CLOSE_HOUR) return null;

    return {start, end};
};

export const isSameDayPickupWindowAvailable = ({allowSameDayPickups, timezone, startTime, endTime, now = new Date()}) => {
    if (!isSameDayPickupAvailable({allowSameDayPickups, timezone, now})) {
        return false;
    }

    const start = moment(startTime, ['HH:mm', 'h:mm a']);
    const end = moment(endTime, ['HH:mm', 'h:mm a']);
    if (!start.isValid() || !end.isValid()) {
        return false;
    }

    const originNow = momentTz.tz(now, timezone);
    const earliestWindow = getEarliestSameDayPickupWindow({timezone, now});
    const pickupStart = originNow.clone().startOf('day').hour(start.hour()).minute(start.minute());
    const pickupEnd = originNow.clone().startOf('day').hour(end.hour()).minute(end.minute());
    return pickupStart.hour() >= BUSINESS_OPEN_HOUR
        && earliestWindow && pickupStart.isSameOrAfter(earliestWindow.start)
        && pickupEnd.isSameOrBefore(originNow.clone().startOf('day').hour(BUSINESS_CLOSE_HOUR))
        && pickupStart.clone().add(MINIMUM_PICKUP_WINDOW_HOURS, 'hours').isSameOrBefore(pickupEnd);
};

export const getCustomerCurrentDate = ({timezone, now = new Date()}) => {
    return timezone && momentTz.tz.zone(timezone)
        ? momentTz.tz(now, timezone).format('YYYY-MM-DD')
        : null;
};

export const getDefaultPickupDate = ({allowSameDayPickups, timezone, now = new Date()}) => {
    if (isSameDayPickupAvailable({allowSameDayPickups, timezone, now})) {
        return momentTz.tz(now, timezone).format('YYYY-MM-DD');
    }

  const currentDate = timezone && momentTz.tz.zone(timezone)
        ? momentTz.tz(now, timezone)
        : moment(now);

    return getNextBusinessDay(currentDate).format('YYYY-MM-DD');
};

export const getDefaultPickupDateForOrigin = ({allowSameDayPickups, origin, now = new Date()}) => {
    const timezone = origin && origin.timezone;

    if (!timezone || !momentTz.tz.zone(timezone)) {
        return getDefaultPickupDate({allowSameDayPickups: 0, now});
    }

    return getDefaultPickupDate({allowSameDayPickups, timezone, now});
};

export const NMFC_Regex = /^\d{4,6}(-\d{1,2})?$/;

export const NMFC_Mask = createTextMask({
    pattern: '999999999',
    allowEmpty: true,
    guide: false,
    maskDefinitions: {
        9: {
            regExp: /[0-9-]/
        }
    }
});
function swapLtlToTl(currentData, dispatch) {
    const itemToPaletteMap = {
        2: 6, //create
        3: 7, //box
    };

    const isHazmat = lodash.get(currentData, 'Extra_HAZM', false);

    const items = currentData.items.map(item => (
        {
            weightMeasurement: item.weightUnit,
            productMeasurement: item.dimUnit,
            noOfItem: 1,
            palleteType: item.palleteType === "" ? itemToPaletteMap[item.itemType] : item.palleteType,
            length: item.length,
            width: item.width,
            noOfHandlingUnit: item.howManyHandlingUnits,
            weightPerHandlingUnit: item.weightPerHandlingUnit,
            height: item.height
        }
    ));

    const accessorials = { hazmat: isHazmat };

    dispatch(reduxFormChange('TlQuoteForm', 'selectCustomer', currentData['selectCustomer']));
    dispatch(reduxFormChange('TlQuoteForm', 'shippingFromPickUpDate', currentData.shippingFromPickUpDate || ''));
    dispatch(reduxFormChange('TlQuoteForm', 'pickupAddress', currentData['shippingFromZip']));
    dispatch(reduxFormChange('TlQuoteForm', 'dropAddress', currentData['shippingToZip']));
    dispatch(reduxFormChange('TlQuoteForm', 'accessorials', accessorials));
    dispatch(reduxFormChange('TlQuoteForm', 'items', items));
}

function swapTlToLtl(currentData, dispatch, freightClassList) {
    const paletteToItemMap = {
        6: 2, //create
        7: 3, //box
    };
    const isHazmat = lodash.get(currentData, 'accessorials.hazmat', false);
    const pickupAddress = lodash.get(currentData, 'pickupAddress', {});
    const dropAddress = lodash.get(currentData, 'dropAddress', {});
    const items = currentData.items.map(item => {
        const palleteType = item.palleteType || 2;
        const itemType = paletteToItemMap[palleteType] || 1;
        const itemData = {
            weightUnit: item.weightMeasurement,
            dimUnit: item.productMeasurement,
            itemType: itemType,
            dimensionType: true,
            palleteType: palleteType,
            length: item.length,
            width: item.width,
            "haz-matItem": isHazmat,
            howManyHandlingUnits: item.noOfHandlingUnit,
            weightPerHandlingUnit: item.weightPerHandlingUnit,
            height: item.height
        };

        if (palleteType === 6) {
            itemData.packagingType = CRATE_ID; // Crate
            itemData.palleteType = '';
        } else if (palleteType === 7) {
            itemData.packagingType = BOX_ID; // Box
            itemData.palleteType = '';
        } else {
            itemData.packagingType = palleteType;
            itemData.palleteType = palleteType;
        }

        return {
            ...itemData,
            ...calculateFreightClass(itemData, freightClassList)
        }
    });

    dispatch(reduxFormChange('LtlQuoteForm', 'selectCustomer', currentData['selectCustomer']));

    dispatch(reduxFormChange('LtlQuoteForm', 'shippingFromPostalCode', lodash.get(pickupAddress, 'zip')));
    dispatch(reduxFormChange('LtlQuoteForm', 'shipmentFromOffset', lodash.get(pickupAddress, 'shipmentFromOffset')));
    dispatch(reduxFormChange('LtlQuoteForm', 'originCountryId', lodash.get(pickupAddress, 'countryId')));
    dispatch(reduxFormChange('LtlQuoteForm', 'originStateId', lodash.get(pickupAddress, 'stateId')));
    dispatch(reduxFormChange('LtlQuoteForm', 'originState', lodash.get(pickupAddress, 'state')));
    dispatch(reduxFormChange('LtlQuoteForm', 'originCity', lodash.get(pickupAddress, 'city')));

    dispatch(reduxFormChange('LtlQuoteForm', 'shippingToPostalCode', lodash.get(dropAddress, 'zip')));
    dispatch(reduxFormChange('LtlQuoteForm', 'shipmentToOffset', lodash.get(dropAddress, 'shipmentToOffset')));
    dispatch(reduxFormChange('LtlQuoteForm', 'destinationCountryId', lodash.get(dropAddress, 'countryId')));
    dispatch(reduxFormChange('LtlQuoteForm', 'destinationStateId', lodash.get(dropAddress, 'stateId')));
    dispatch(reduxFormChange('LtlQuoteForm', 'destinationState', lodash.get(dropAddress, 'state')));
    dispatch(reduxFormChange('LtlQuoteForm', 'destinationCity', lodash.get(dropAddress, 'city')));

    dispatch(reduxFormChange('LtlQuoteForm', 'shippingFromPickUpDate', currentData.shippingFromPickUpDate || ''));
    dispatch(reduxFormChange('LtlQuoteForm', 'shippingToDeliveryDate', currentData.shippingToDeliveryDate || ''));
    dispatch(reduxFormChange('LtlQuoteForm', 'shippingFromZip', pickupAddress));
    dispatch(reduxFormChange('LtlQuoteForm', 'shippingToZip', dropAddress));
    dispatch(reduxFormChange('LtlQuoteForm', 'Extra_HAZM', isHazmat));
    dispatch(reduxFormChange('LtlQuoteForm', 'items', items));
}

function calculateFreightClass(item, freightClassList) {
    let h = parseInt(item.height);
    let w = parseInt(item.width);
    let l = parseInt(item.length);
    let weight = parseFloat(item.weightPerHandlingUnit);

    if (h > -1 && w > -1 && l > -1 && weight > -1) {
        // kg to lb
        if (item.weightUnit === 1) {
            weight = item.weightPerHandlingUnit * 2.20462;
        }

        // inches to feet
        if (item.dimUnit === 2) {
            w /= 12;
            h /= 12;
            l /= 12;
        }

        let density = Number(
            parseFloat(
                weight / ((l * w * h))
            ).toFixed(2)
        );
        let fClass = '';

        if (density < 1) {
            fClass = "400";
        } else if (density >= 1 && density < 2) {
            fClass = "300";
        } else if (density >= 2 && density < 4) {
            fClass = "250";
        } else if (density >= 4 && density < 6) {
            fClass = "175";
        } else if (density >= 6 && density < 8) {
            fClass = "125";
        } else if (density >= 8 && density < 10) {
            fClass = "100";
        } else if (density >= 10 && density < 12) {
            fClass = "92.5";
        } else if (density >= 12 && density < 15) {
            fClass = "85";
        } else if (density >= 15 && density < 22.5) {
            fClass = "70";
        } else if (density >= 22.5 && density < 30) {
            fClass = "65";
        } else if (density >= 30 && density < 35) {
            fClass = "60"
        } else if (density >= 35 && density < 50) {
            fClass = "55"
        } else {
            fClass = "50"
        }

        const calculatedClass = freightClassList.find(x => x.name === fClass);

        return {
            poundsPerCubicFeet: density,
            calculatedFreightClass: calculatedClass,
            previousCalculatedFreightClass: calculatedClass
        };
    }

    return {};
}

export function swapDataBetweenQuote(
    currentData = {},
    typeTo,
    typeFrom,
    dispatch,
    freightClassList
) {
    if (typeFrom === LTL_QUOTE_FORM && typeTo === TL_QUOTE_FORM) {
        return swapLtlToTl(currentData, dispatch);
    }

    if (typeFrom === TL_QUOTE_FORM && typeTo === LTL_QUOTE_FORM) {
        return swapTlToLtl(currentData, dispatch, freightClassList);
    }
}

export const getCcProcessingFeeFinancialSettings = (financialSetting = []) => {
    return {
        ccProcessingFeePercent: lodash.get(
            financialSetting.find(x => x.id === FinancialSettingsMapping.creditCardProcessingFee),
            'financialSetting',
            0.00
        ),
        ccProcessingFeeThreshold: lodash.get(
            financialSetting.find(x => x.id === FinancialSettingsMapping.creditCardProcessingFeeThreshold),
            'financialSetting',
            1000000.00
        ),
    };
};

export const getCancellationFinancialSettings = (financialSetting = [], shippingModeId) => {
    const isTlShipment = shippingModeId === TL_SHIPMENT_MODE;
    const refundFeeSettingId = isTlShipment
        ? FinancialSettingsMapping.cancelFeeTL
        : FinancialSettingsMapping.cancelFeeLTL;
    const storeCreditFeeSettingId = isTlShipment
        ? FinancialSettingsMapping.cancelFeeTLStoreCredit
        : FinancialSettingsMapping.cancelFeeLTLStoreCredit;

    const refundFeePercent = Number(
        lodash.get(
        financialSetting.find(item => item.id === refundFeeSettingId),
        'financialSetting',
        0
        )
    );

    const storeCreditFeePercent = Number(
        lodash.get(
            financialSetting.find(item => item.id === storeCreditFeeSettingId),
            'financialSetting',
            0
        )
    );
    const storeCreditExpDays = Number(
        lodash.get(
            financialSetting.find(item => item.id === FinancialSettingsMapping.storeCreditExpDays),
            'financialSetting',
            0
        )
    );

    return {
        refundFeePercent,
        refundPercent: Math.max(0, 100 - refundFeePercent),
        storeCreditFeePercent,
        storeCreditPercent: Math.max(0, 100 - storeCreditFeePercent),
        storeCreditExpDays
    };
};

export const getProcessingFeeForAmount = (amount, financialSetting = []) => {
    const ccProcessingFeeFinancialSettings = getCcProcessingFeeFinancialSettings(financialSetting);
    const ccProcessingFeeThreshold = parseFloat(
        lodash.get(ccProcessingFeeFinancialSettings, 'ccProcessingFeeThreshold', 1000000.00)
    );
    const ccProcessingFeePercent = parseFloat(
        lodash.get(ccProcessingFeeFinancialSettings, 'ccProcessingFeePercent', 0.00)
    );
    const amt = parseFloat(amount || 0.00);

    if (isNaN(amt) || amt < ccProcessingFeeThreshold) return 0.00;

    return Math.ceil(amt * ccProcessingFeePercent / 100.00);
};

export const getDateTime = (datetime, format = DATETIME_FORMAT) => {
    return moment(datetime||undefined).format(format)
}

export const setSession = (key, value) => {
    localStorage.setItem(key, value)
};

export const getSession = (key) => {
    return localStorage.getItem(key)
};

export const removeSession = (key) => {
    localStorage.removeItem(key)
};

export const setGaUserId = (customerId) => {
    window.gtag('set', {user_id: customerId ? String(customerId) : null});
};

export const clearSession = () => {
    setGaUserId(null);
    localStorage.clear()
};

export const sort = (list, key) => {
    let listToreturn = list||[];
    listToreturn.sort(function (a, b) {
        if (a[key] < b[key]) return -1;
        if (a[key] > b[key]) return 1;
        return 0;
    })
    return listToreturn;
}

export const deepCompare = (x, y) => {
    var i, l, leftChain, rightChain;
    function compare2Objects(x, y) {
        var p;

        // remember that NaN === NaN returns false
        // and isNaN(undefined) returns true
        if (isNaN(x) && isNaN(y) && typeof x === 'number' && typeof y === 'number') {
            return true;
        }

        // Compare primitives and functions.
        // Check if both arguments link to the same object.
        // Especially useful on the step where we compare prototypes
        if (x === y) {
            return true;
        }

        // Works in case when functions are created in constructor.
        // Comparing dates is a common scenario. Another built-ins?
        // We can even handle functions passed across iframes
        if ((typeof x === 'function' && typeof y === 'function') ||
            (x instanceof Date && y instanceof Date) ||
            (x instanceof RegExp && y instanceof RegExp) ||
            (x instanceof String && y instanceof String) ||
            (x instanceof Number && y instanceof Number)) {
            return x.toString() === y.toString();
        }

        // At last checking prototypes as good as we can
        if (!(x instanceof Object && y instanceof Object)) {
            return false;
        }

        if (x.isPrototypeOf(y) || y.isPrototypeOf(x)) {
            return false;
        }

        if (x.constructor !== y.constructor) {
            return false;
        }

        if (x.prototype !== y.prototype) {
            return false;
        }

        // Check for infinitive linking loops
        if (leftChain.indexOf(x) > -1 || rightChain.indexOf(y) > -1) {
            return false;
        }

        // Quick checking of one object being a subset of another.
        // todo: cache the structure of arguments[0] for performance
        for (p in y) {
            if (y.hasOwnProperty(p) !== x.hasOwnProperty(p)) {
                return false;
            }
            else if (typeof y[p] !== typeof x[p]) {
                return false;
            }
        }

        for (p in x) {
            if (y.hasOwnProperty(p) !== x.hasOwnProperty(p)) {
                return false;
            }
            else if (typeof y[p] !== typeof x[p]) {
                return false;
            }

            switch (typeof (x[p])) {
                case 'object':
                case 'function':

                    leftChain.push(x);
                    rightChain.push(y);

                    if (!compare2Objects(x[p], y[p])) {
                        return false;
                    }

                    leftChain.pop();
                    rightChain.pop();
                    break;

                default:
                    if (x[p] !== y[p]) {
                        return false;
                    }
                    break;
            }
        }

        return true;
    }

    if (arguments.length < 1) {
        return true; //Die silently? Don't know how to handle such case, please help...
        // throw "Need two or more arguments to compare";
    }

    for (i = 1, l = arguments.length; i < l; i++) {

        leftChain = []; //Todo: this can be cached
        rightChain = [];

        if (!compare2Objects(arguments[0], arguments[i])) {
            return false;
        }
    }

    return true;
}


export const setCookie = (key, value) => {
    var d = new Date();
    d.setTime(d.getTime() + (30 * 24 * 60 * 60 * 1000));
    var expires = "expires=" + d.toUTCString();
    document.cookie = key + "=" + value + ";" + expires + ";path=/;secure";
};

export const getCookie = (key) => {
    var name = key + "=";
    var ca = document.cookie.split(';');
    for (var i = 0; i < ca.length; i++) {
        var c = ca[i];
        while (c.charAt(0) == ' ') {
            c = c.substring(1);
        }
        if (c.indexOf(name) == 0) {
            return c.substring(name.length, c.length);
        }
    }
    return "";
};

export const removeCookie = (key) => {
    var d = new Date();
    d.setTime(d.getTime() + (30 * 24 * 60 * 60 * 1000));
    var expires = "expires=" + 'Thu, 01 Jan 1970 00:00:00 UTC';
    document.cookie = key + "=" + ";" + expires + ";path=/";
};


export function logMessage(key, value) {
    console.info(key + ' =>', value);
}

export function nullCheck(value) {
    return value && value != '' && value != undefined && value != false
}


export function showNotification(code, dictionary, lang, flag, type = 'info') {
    if (code.length > 15 && !flag) {
        toast[type](<Toaster msg={code} type={type} />)
    } else {
        let textObj = dictionary[lang][code.code ? code.code : code]
        if (textObj) {
            if (textObj.type) {
                toast[textObj.type](<Toaster msg={textObj.text} type={textObj.type} />)
            } else {
                toast['error'](<Toaster msg={textObj} type={'error'} />)
            }
        } else {
            toast['error'](<Toaster msg={code} type={'error'} />)
        }
    }
}

function hideNotification(id){
    toast.dismiss(id);
}

export function showAgentNotification(text, type , textType) {
  // notification_classificationtinyint(3)  1->Red 2->Blue/Neutral 3->Green 4->Yello
  let t = 'error'
  if (type === 2) {
    t = 'success'
  }
  if (type === 3) {
    t = 'info'
  }
  if(type === 4){
    t = 'warn'
  }
  toast[t](<Toaster msg={text} type={t} textType={textType} />, { hideProgressBar: true, autoClose: true })
}

export function getInputClasses(requiredClass, touched, error, disabled) {
    if (disabled) {
        return 'form-inputFieldrdonly'
    }
    else {
        if (touched && error) {
            if (requiredClass)
                return 'form-inputField form-inputFieldError form-inputFieldImp'
            return 'form-inputField form-inputFieldError'
        } else {
            if (requiredClass)
                return 'form-inputField form-inputFieldImp'
            return 'form-inputField'
        }
    }

}

export const phoneMask = createTextMask({
    pattern: '(999) 999-9999',
});

export const phoneMask12 = createTextMask({
    pattern: '(99) 999-999-9999',
});
export const phoneMask13 = createTextMask({
    pattern: '(99) 999-999-99999',
});
export const phoneMask14 = createTextMask({
    pattern: '(99) 999-999-999999',
});
export const phoneMask15 = createTextMask({
    pattern: '(99) 999-999-9999999',
});


export const numberMask = createTextMask({
    pattern: '999',
});

export const expiryDateMask = createTextMask({
    pattern: '99/99',
});

export const percentMask = createTextMask({
    pattern: '99%',
});

// export const currencyMask = createTextMask({
//     pattern: '$999.99',
// });
export const fourDigitMask = createTextMask({
    pattern: '9999',
});

export const FiveDigitMask = createTextMask({
    pattern: '99999',
});

export const SixDigitMask = createTextMask({
    pattern: '999999',
});

export const visaMask = createTextMask({
    pattern: '9999-9999-9999-9999',
});

export const percentsMask = createNumberMask({
    suffix: "%",
    decimalPlaces: 2,
    locale: "en-US",
    maximumFractionDigits: 3
});

export const TenDecimalMask = createNumberMask({
    decimalPlaces: 2,
    maximumFractionDigits: 8
});

export const EightDecimalMask = createNumberMask({
    decimalPlaces: 2,
    maximumFractionDigits: 6
});

export const TwoDecimalMask = createNumberMask({
    decimalPlaces: 2,
    maximumFractionDigits: 2,
});

const currencyMask = createNumberMask({
    prefix: "$",
    decimalPlaces: 2,
    locale: "en-US",
    maximumFractionDigits: 4
});

const costMask = createNumberMask({
    prefix: "$",
    decimalPlaces: 2,
    locale: "en-US",
    maximumFractionDigits: 8
});

const NineDigitMask = createNumberMask({
    prefix: "$",
    decimalPlaces: 2,
    locale: "en-US",
    maximumFractionDigits: 9
});

const declaredValueMask = createNumberMask({
    prefix: "$ ",
    decimalPlaces: 2,
    locale: "en-US",
    maximumFractionDigits: 6
})


const currencyMask4 = createNumberMask({
    prefix: "$",
    decimalPlaces: 4,
    locale: "en-US",
    maximumFractionDigits: 4
});

const enNumberMask = createNumberMask({
    decimalPlaces: 2,
    locale: "en-US",
    maximumFractionDigits: 4,
    allowEmpty: true
});

const numberMaskWhole = createNumberMask({
    locale: "en-US",
    maximumFractionDigits: 4,
    allowEmpty: true
});

const dollarMask = createNumberMask({
    prefix: "$",
    locale: "en-US",
    maximumFractionDigits: 4,
    allowEmpty: true
});

export const getLengthForNumber = (length = 1) => {
    return (''
        .padEnd(length,'1')
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        .match(/,/g) || []).length+length;
}



export function getMappedDropdownOption(masterData, values) {
    if (!masterData || masterData.length == 0) {
        return { text: '', value: '' }
    }
    if (Array.isArray(values)) {
        let value = []
        values.map((x) => {
            let filter = []
            if (x.value) {
                filter = masterData.filter(y => y.id == x.value)
            } else {
                filter = masterData.filter(y => y.id == x)
            }
            if (filter.length > 0) {
                value.push({
                    text: filter[0].name,
                    value: filter[0].id
                })
            }
        })
        return value
    } else {

        if (values.value) {
            return {
                text: masterData.filter(x => x.id == values.value)[0].name,
                value: masterData.filter(x => x.id == values.value)[0].id,
            }
        } else {
            return {
                text: masterData.filter(x => x.id == values)[0].name,
                value: masterData.filter(x => x.id == values)[0].id,
            }
        }
    }
}


export function getMappedSelectBoxOption(masterData, values) {
    if (!masterData || masterData.length == 0) {
        return { id: '', name: '' }
    }
    if (Array.isArray(values)) {
        let value = []
        values.map((x) => {
            let filter = []
            if (x.id) {
                filter = masterData.filter(y => y.id == x.id)
            } else {
                filter = masterData.filter(y => y.id == x)
            }
            if (filter.length > 0) {
                value.push({
                    id: filter[0].id,
                    name: filter[0].name
                })
            }
        })
        return value
    } else {

        if (values.id) {
            return {
                id: masterData.filter(x => x.id == values.id)[0].id,
                name: masterData.filter(x => x.id == values.id)[0].name
            }
        } else {
            return {
                id: masterData.filter(x => x.id == values)[0].id,
                name: masterData.filter(x => x.id == values)[0].name,
            }
        }
    }
}

export function checkDate(date) {
    let now = moment().format().substring(0, 10);
    let currentYear = parseInt(now.split('-')[0])
    let split = date.split('-')
    if (parseInt(split[0]) < (currentYear - 50) || parseInt(split[0]) > (currentYear + 50)) {
        return true
    } else {
        return false
    }
}

export function yearRange() {
    let now = moment().format().substring(0, 10);
    let currentYear = parseInt(now.split('-')[0])
    let pastYear = Array.apply(null, { length: 50 }).map(function (value, index) {
        return { id: (-index + currentYear), value: (-index + currentYear), name: (-index + currentYear) };
    });
    pastYear.reverse();
    pastYear.pop()
    let futureYear = Array.apply(null, { length: 50 }).map(function (value, index) {
        return { id: (index + currentYear), value: (index + currentYear), name: (index + currentYear) };
    });
    return pastYear.concat(futureYear)
}

export function getUnitConversion(value, type) {
    switch (type) {
        case 1: return value;
        case 2: return (value / 12).toFixed(2);
        case 3: return (value / 30.48).toFixed(2);
    }
}

export const creditCardPhoneMask = createTextMask({
    pattern: '99-999-999-999999',
});

export const formattedPhoneNo = (phoneNo)=>{
    if(phoneNo && (phoneNo.toString()).length >=10){
        let val = phoneNo.toString();
        return `(${val.substring(0,3)}) ${val.substring(3,6)}-${val.substring(6,val.length+1)}`
    }
    return phoneNo;
}

export function checkCreditCard(cardnumber, cardname) {

    var ccErrorNo = 0;
    var ccErrors = new Array()

    ccErrors[0] = "Unknown card type";
    ccErrors[1] = "No card number provided";
    ccErrors[2] = "Credit card number is in invalid format";
    ccErrors[3] = "Credit card number is invalid";
    ccErrors[4] = "Credit card number has an inappropriate number of digits";
    ccErrors[5] = "Warning! This credit card number is associated with a scam attempt";


    // Array to hold the permitted card characteristics
    var cards = new Array();

    // Define the cards we support. You may add addtional card types as follows.

    //  Name:         As in the selection box of the form - must be same as user's
    //  Length:       List of possible valid lengths of the card number for the card
    //  prefixes:     List of possible prefixes for the card
    //  checkdigit:   Boolean to say whether there is a check digit

    cards[0] = {
        name: "Visa",
        length: "13,16,19",
        prefixes: "4",
        checkdigit: true
    };
    cards[1] = {
        name: "MasterCard",
        length: "16",
        prefixes: "51,52,53,54,55,22,23,24,25,26,27",
        checkdigit: true
    };
    cards[2] = {
        name: "DinersClub",
        length: "14,16",
        prefixes: "36,38,54,55",
        checkdigit: true
    };
    cards[3] = {
        name: "CarteBlanche",
        length: "14",
        prefixes: "300,301,302,303,304,305",
        checkdigit: true
    };
    cards[4] = {
        name: "AmEx",
        length: "15",
        prefixes: "34,37",
        checkdigit: true
    };
    cards[5] = {
        name: "Discover",
        length: "16,19",
        prefixes: "30,36,38,39,60,62,64,65,81",
        checkdigit: true
    };
    cards[6] = {
        name: "JCB",
        length: "16",
        prefixes: "35",
        checkdigit: true
    };
    cards[7] = {
        name: "enRoute",
        length: "15",
        prefixes: "2014,2149",
        checkdigit: true
    };
    cards[8] = {
        name: "Solo",
        length: "16,18,19",
        prefixes: "6334,6767",
        checkdigit: true
    };
    cards[9] = {
        name: "Switch",
        length: "16,18,19",
        prefixes: "4903,4905,4911,4936,564182,633110,6333,6759",
        checkdigit: true
    };
    cards[10] = {
        name: "Maestro",
        length: "12,13,14,15,16,18,19",
        prefixes: "5018,5020,5038,6304,6759,6761,6762,6763",
        checkdigit: true
    };
    cards[11] = {
        name: "VisaElectron",
        length: "16",
        prefixes: "4026,417500,4508,4844,4913,4917",
        checkdigit: true
    };
    cards[12] = {
        name: "LaserCard",
        length: "16,17,18,19",
        prefixes: "6304,6706,6771,6709",
        checkdigit: true
    };

    // Establish card type
    var cardType = -1;
    for (var i = 0; i < cards.length; i++) {

        // See if it is this card (ignoring the case of the string)
        if (cardname.toLowerCase() == cards[i].name.toLowerCase()) {
            cardType = i;
            break;
        }
    }

    // If card type not found, report an error
    if (cardType == -1) {
        ccErrorNo = 0;
        return false;
    }

    // Ensure that the user has provided a credit card number
    if (cardnumber.length == 0) {
        ccErrorNo = 1;
        return false;
    }

    // Now remove any spaces from the credit card number
    cardnumber = cardnumber.replace(/\s/g, "");

    // Check that the number is numeric
    var cardNo = cardnumber
    var cardexp = /^[0-9]{13,19}$/;
    if (!cardexp.exec(cardNo)) {
        ccErrorNo = 2;
        return false;
    }

    // Now check the modulus 10 check digit - if required
    // if (cards[cardType].checkdigit) {
    //     var checksum = 0;                                  // running checksum total
    //     var mychar = "";                                   // next char to process
    //     var j = 1;                                         // takes value of 1 or 2

    //     // Process each digit one by one starting at the right
    //     var calc;
    //     for (i = cardNo.length - 1; i >= 0; i--) {

    //         // Extract the next digit and multiply by 1 or 2 on alternative digits.
    //         calc = Number(cardNo.charAt(i)) * j;

    //         // If the result is in two digits add 1 to the checksum total
    //         if (calc > 9) {
    //             checksum = checksum + 1;
    //             calc = calc - 10;
    //         }

    //         // Add the units element to the checksum total
    //         checksum = checksum + calc;

    //         // Switch the value of j
    //         if (j == 1) { j = 2 } else { j = 1 };
    //     }

    //     // All done - if checksum is divisible by 10, it is a valid modulus 10.
    //     // If not, report an error.
    //     if (checksum % 10 != 0) {
    //         ccErrorNo = 3;
    //         return false;
    //     }
    // }

    // Check it's not a spam number
    if (cardNo == '5490997771092064') {
        ccErrorNo = 5;
        return false;
    }

    // The following are the card-specific checks we undertake.
    var LengthValid = false;
    var PrefixValid = false;
    var undefined;

    // We use these for holding the valid lengths and prefixes of a card type
    var prefix = new Array();
    var lengths = new Array();

    // Load an array with the valid prefixes for this card
    prefix = cards[cardType].prefixes.split(",");

    // Now see if any of them match what we have in the card number
    for (i = 0; i < prefix.length; i++) {
        var exp = new RegExp("^" + prefix[i]);
        if (exp.test(cardNo)) PrefixValid = true;
    }

    // If it isn't a valid prefix there's no point at looking at the length
    if (!PrefixValid) {
        ccErrorNo = 3;
        return false;
    }

    // See if the length is valid for this card
    lengths = cards[cardType].length.split(",");
    for (var j = 0; j < lengths.length; j++) {
        if (cardNo.length == lengths[j]) LengthValid = true;
    }

    // See if all is OK by seeing if the length was valid. We only check the length if all else was
    // hunky dory.
    if (!LengthValid) {
        ccErrorNo = 4;
        return false;
    };

    // The credit card is in the required format.
    return true;
}

export const b64toBlob = (b64Data, contentType, sliceSize)=> {
    contentType = contentType || '';
    sliceSize = sliceSize || 512;

    var byteCharacters = atob(b64Data);
    var byteArrays = [];

    for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      var slice = byteCharacters.slice(offset, offset + sliceSize);

      var byteNumbers = new Array(slice.length);
      for (var i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      var byteArray = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
    }

    var blob = new Blob(byteArrays, {type: contentType});
    return blob;
  }


export const getPhoneMask = (phoneNumber)=>{
    if(phoneNumber){
        switch(phoneNumber.length){
            case 10:return phoneMask;
            case 12:return phoneMask12;
            case 13:return phoneMask13;
            case 14:return phoneMask14;
            case 15:return phoneMask15;
            return {}

        }
    }
    return {}
}

export const convertBase64toBlob = (data)=>{
    if(data){
        var binary = atob(data.replace(/\s/g, ''));
        var len = binary.length;
        var buffer = new ArrayBuffer(len);
        var view = new Uint8Array(buffer);
        for (var i = 0; i < len; i++) {
            view[i] = binary.charCodeAt(i);
        }
        return view;
    }
    return []

}

export const numberSort = (list,key)=>{
    let listToreturn = (list||[]).slice();
    listToreturn.sort(function (a, b) {
        return Number( a[key]||0 ) - Number(b[key] ||0 );
    })
    return listToreturn;
}

export const getBlobURL = (data)=>{
    if(data){
        let dataToBeConverted = data;
        let mimeType = 'application/pdf';
        if(data.includes(';base64,')){
            dataToBeConverted = data.split('base64,')[1]
            mimeType =  data.split('base64,')[0] ? data.split('base64,')[0].split(':')[1] :'application/pdf'
        }
        var binary = atob(dataToBeConverted.replace(/\s/g, ''));
        var len = binary.length;
        var buffer = new ArrayBuffer(len);
        var view = new Uint8Array(buffer);
        for (var i = 0; i < len; i++) {
            view[i] = binary.charCodeAt(i);
        }


        var blob = new Blob([view], { type:mimeType });
        blob.fileName = 'abc.pdf'
        return URL.createObjectURL(blob)
    }
    else{
        return ''
    }

}

export const getCountryPhonePrefix = (countryList,country) => {
    if(!country || !countryList || !Array.isArray(countryList)||(countryList.length<1)){
        return ''
    }
    else{
        let countryId = country;
        if(typeof(country)=='object'){ // country as {id,name}
            countryId = country.id
        }
        let countryObj = countryList.find(x=>x.id==countryId);
        if(!countryObj || !countryObj.phonePrifix){
            return ''
        }
        return `(${countryObj.phonePrifix}) `
    }
}

export const joinWithAnd = values => {
    if (!Array.isArray(values) || values.length === 0) {
        return '';
    }

    if (values.length === 1) {
        return values[0];
    }

    return `${values.slice(0, -1).join(', ')} and ${values[values.length - 1]}`;
};

export const formatEmptyFieldsSummary = sections => {
    const totalEmptyFields = sections.reduce((total, section) => total + section.fields.length, 0);

    if (totalEmptyFields === 0) {
        return '';
    }

    if (totalEmptyFields > 5) {
        const sectionNames = sections.map(
            (section, index) => `'${section.name}'${index === 0 ? ' section' : ''}`
        );

        return `Please fill out fields in ${joinWithAnd(sectionNames)}`;
    }

    const sectionMessages = sections.map((section, index) => {
        const fieldNames = joinWithAnd(section.fields);
        const sectionSuffix = index === 0 ? ' section' : '';

        return section.fields.length > 1
            ? `${fieldNames} fields in '${section.name}'${sectionSuffix}`
            : `${fieldNames} in '${section.name}'${sectionSuffix}`;
    });

    return `Please fill out ${joinWithAnd(sectionMessages)}`;
};

export const getFormatIncludeCommas = (value = '') => {
  const [val, end] = value.toString().split('.');
  let b = '';

  for (let i = 0; i < val.length; i++) {
    if (!((val.length - i) % 3) && i !== 0) {
      b += ',';
    }
    b += val[i];
  }
  return end ? `${b}.${end}`: b;
}

export const convertObjectToQueryString = (data) => {
    return Object.keys(data).map(key => `${key}=${encodeURIComponent(data[key])}`).join('&');
}

export const decode_utf8 = function(s)  {
    return decodeURIComponent(encodeURIComponent(s));
}

export const isShipmentFullPayed = (shipment) => {
  const charge = Number(shipment.shipmentCharge);
  const payment = Number(shipment.shipmentPayment);
  return payment >= charge;
}

export function isIE() {
  const ua = window.navigator.userAgent;
  const msie = ua.indexOf('MSIE '); // IE 10 or older
  const trident = ua.indexOf('Trident/'); //IE 11

  return (msie > 0 || trident > 0);
}

export function waitUntil(conditionFunction) {
    const poll = resolve => {
        if(conditionFunction()) {
            resolve();
        }else{
            setTimeout(_ => poll(resolve), 1);
        }
    }

    return new Promise(poll);
}

export const getQueryParams = () => {
    let queryParams = window.location.search.replace('?', '').split('&');
    return queryParams.reduce(function(o, val) { let item = val.split('='); o[item[0]] = item[1]; return o; }, {});
}

export const historyPush = (url) => {
    window.history.pushState(null, null, url);
    // ReactGA.initialize(process.env.REACT_APP_GA_ID);
    // ReactGA.ga('set', 'contentGroup2', 'my');
    // ReactGA.set({ page: url });
    // ReactGA.pageview(url);
}

export const isMobileOrTablet = () => {
    let check = false;
    (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor||window.opera);
    check = check || /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/i.test(navigator.userAgent);
    check = check || (lodash.get(navigator, 'maxTouchPoints', 0) > 0);
    return check;
}

export const isMobile = () => {
    return window.matchMedia("(max-width: 767px)").matches;
}

export const standardTimezone = () => {
    return moment(moment().format('YYYY-01-01')).format('Z')
}

export const img = (type, content) => {
    return `data:${type};base64,${content}`;
}

function roundToInteger (value, factor = 1) {
    return Math.ceil(parseFloat(value) * factor)
}

export function convertWeightToPound(measure, weight){
    let factor = 2.20462;
    if (Number(measure) !== 1){
        factor = 1;
    }

    return roundToInteger(weight, factor);
}

export function convertLengthToInch(measure, length){
    let factor = 12;
    if (Number(measure) !== 1){
        factor = 1;
    }

    return roundToInteger(length, factor);
}

export function isCountryWithStates(countryId){
    const parsedCountry = parseInt(countryId);
    if (isNaN(parsedCountry)) { return false; }

    return COUNTRIES_WITH_STATES.includes(parsedCountry);
}

export function isCountryWithStatesFromObj(obj, key){
    return isCountryWithStates(lodash.get(obj, key, 0));
}

export function getStateData(obj, searchKey = 'customer', returnKey='name'){
    //(utilCommon.isCountryWithStates(x.customerCountryId.id)) ?(x.customerStateId ? x.customerStateId.stateCode:''):x.customerState
    let localCountryId = lodash.get(obj, searchKey + 'CountryId.id', 0);

    let out = lodash.get(obj, searchKey + 'State', '');

    if (
        isCountryWithStates(localCountryId) &&
        isObject(lodash.get(obj, searchKey + 'StateId',false))
    ){
        out =  lodash.get(obj, searchKey + 'StateId.'+returnKey, '');
    }
    return out;
}

export const isObject = function(a) {
    return (!!a) && (a.constructor === Object);
};

export function isFtlQuote(search) {
    const urlSearchParams = new URLSearchParams(search);

    if (
        ! urlSearchParams.has('data') ||
        ! urlSearchParams.get('data') ||
        ! isValidJsonString(urlSearchParams.get('data'))
    ) {
        return false;
    }

    const urlParamsObj = JSON.parse(urlSearchParams.get('data'));

    return lodash.has(urlParamsObj, 'formName') &&
        lodash.has(urlParamsObj, 'fid') &&
        urlParamsObj.formName === 'TL' &&
        ['form-ftl-short', 'form-ftl-long'].includes(urlParamsObj.fid);
}

export function isSpamFtlQuote(search) {
    const data = JSON.parse((new URLSearchParams(search)).get('data'));

    return isFtlQuote(search) && (! lodash.has(data, 'wght') || ! (/\d/).test(data.wght));
}

export function isLtlQuote(url) {
    let fc_form_type_used = '';
    try {
        let match_cookie = document.cookie.match(new RegExp('(^| )' + 'fc_form_type_used' + '=([^;]+)'));
        if (match_cookie) fc_form_type_used = match_cookie[2];
    } catch (e) {
        //
    }

    return (url === '/home/freight-quote' && ! lodash.isEmpty(sessionStorage.getItem('quoteSearchString'))) || fc_form_type_used === 'LTL - Short'
}

export const moneyFormat = function(data, FractionDigits = 2){
    let value = parseFloat(data);
    if (isNaN(value)) { value = 0; }

    const formatter = new Intl.NumberFormat('en-US',
        {
            // style: 'currency',
            // currency: 'USD',
            minimumFractionDigits: FractionDigits,
        }
    );

    return formatter.format(value);
}

export const dollarFormat = function (data){
    const money = moneyFormat(data, 2);
    return money.substr(0, money.length - 3);
}

export const centsFormat = function(data){
    const money = moneyFormat(data, 2);
    return money.substr(money.length - 2);
}

export const isValidJsonString = (value) => {
    try {
        return typeof JSON.parse(value) === 'object'
    } catch (e) {
        return false;
    }
}

export const isInvalidMonth = (month, year) => {
  let d = new Date();
  let m = d.getMonth() + 1;
  let y = parseInt(d.getFullYear().toString().slice(-2))
  if (year == y && month < m) {
    return true
  } else {
    return false
  }
}

export const isInvalidYear = (year) => {
  let d = new Date();
  let y = parseInt(d.getFullYear().toString().slice(-2))
  if (year < y) {
    return true
  } else {
    return false
  }
}

export const setGoogleAdsId = () => {
    const gclid = getCookie('gclid');
    const gbraid = getCookie('gbraid');
    const wbraid = getCookie('wbraid');
    const utmCampaign = getCookie('utm_campaign');
    const utmSource = getCookie('utm_source');

    if (gclid !== '') sessionStorage.setItem('gclid', gclid);
    if (gbraid !== '') sessionStorage.setItem('gbraid', gbraid);
    if (wbraid !== '') sessionStorage.setItem('wbraid', wbraid);
    if (utmCampaign !== '') sessionStorage.setItem('utmCampaign', utmCampaign);
    if (utmSource !== '') sessionStorage.setItem('utmSource', utmSource);
}

export const addGoogleAdsId = (inputDTO) => {
    if (!lodash.isEmpty(sessionStorage.getItem('gclid'))) {
        inputDTO.adsId = sessionStorage.getItem('gclid');
        inputDTO.adsIdType = 'gclid';
    }

    if (!lodash.isEmpty(sessionStorage.getItem('gbraid'))) {
        inputDTO.adsId = sessionStorage.getItem('gbraid');
        inputDTO.adsIdType = 'gbraid';
    }

    if (!lodash.isEmpty(sessionStorage.getItem('wbraid'))) {
        inputDTO.adsId = sessionStorage.getItem('wbraid');
        inputDTO.adsIdType = 'wbraid';
    }

    return inputDTO;
}

export const isEmpty = (value) => {
    if (value === null || value === undefined) {
        return true;
    }

    if (typeof value === 'string' || Array.isArray(value)) {
        return value.length === 0;
    }

    if (typeof value === 'object') {
        return Object.keys(value).length === 0;
    }

    return false;
};

export const convertToUsaCurrencyFormat = currency => {
    let minimumFractionDigits;
    let maximumFractionDigits;

    const sign = currency < 0 ? '-' : '';
    currency = Math.abs(currency);

    if (Number.isInteger(currency)) {
        minimumFractionDigits = 0;
        maximumFractionDigits = 0;
    } else {
        const fixedDigits = currency.toFixed(2);

        if (fixedDigits.endsWith('.00')) {
            minimumFractionDigits = 0;
            maximumFractionDigits = 0;
        } else {
            minimumFractionDigits = 2;
            maximumFractionDigits = 2;
        }
    }

    const formattedValue = new Intl.NumberFormat(
        'en-US',
        {minimumFractionDigits, maximumFractionDigits},
    ).format(currency);

    return `${sign}${formattedValue}`;
};

export const getFreightQuotePath = (searchParams, initialPath = '/home/freight-quote') => {
    return initialPath + (lodash.startsWith(searchParams, '?') ? '' : '?') + searchParams
}

export const sendJourneyEvent = (eventType, params = {}) => {
    const url = `${process.env.REACT_APP_API_URL}/v2/journey-event`;
    const config = {headers: authHeader()}

    axios.post(url, {eventType, params}, config).catch(() => {});
}

export const getQuoteItemMaxDimInInches = (product) => {
    let factor = Number(product.dimUnit) === 2 ? 1 : 12;
    return factor * (Number(product['length']) > Number(product.width)
        ? Number(product['length']) : Number(product.width));
}

export const getElsFactor = (productsMaxDimInInches) => {
    if (Number(productsMaxDimInInches) < 96 || Number(productsMaxDimInInches) >= 348) {
        return 0;
    }

    let elsFactor = Math.floor(Number(productsMaxDimInInches)/12);
    elsFactor = !isNaN(elsFactor) ? elsFactor : 0;

    return elsFactor;
}

export const getQuoteItemsMaxLength = (products) => {
    let productsMaxLength = [];
    products.map(
        product => {
            productsMaxLength.push(getQuoteItemMaxDimInInches(product));
        }
    );

    return getElsFactor(lodash.max(productsMaxLength));
}

export const isFloat = value => {
    return !Number.isInteger(value) && Number.isFinite(value);
}

export const filterChars = (value, type ) => {
    let result = '';

    for (let i = 0; i < value.length; i++) {
        if ( type === INPUT_TYPE_NUMBER ) {
            if (Number_Regex.test(value[i])) {
                result += value[i];
            }
        }else if ( type === INPUT_TYPE_FLOAT ) {
            if (Float_Regex.test(value[i])) {
                result += value[i];
            }
        }else if ( type === INPUT_TYPE_TEXT_WITHOUT_SP_SYMBOLS ){
            if (AlphaNumericWhiteSpace_Regex.test(value[i])) {
                result += value[i];
            }
        }else{
            let c = value.charCodeAt(i);
            if ( (c >= 32 && c <= 127) || c === 10) {
                result += String.fromCharCode(c);
            }
        }
    }

    return result;
}

export const isCardExpired = (month, year) => {
    month = parseInt(month);
    year = parseInt(year);

    const date = new Date();
    const currentYear = parseInt(date.getFullYear().toString().substr(-2));
    const currentMonth = date.getMonth() + 1;

    if (year < currentYear) {
        return true;
    }

    if (year === currentYear && month <= currentMonth) {
        return true;
    }

    return false;
};

export const generatePass = () => {
    let pass = '';
    let str = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ' +
        'abcdefghijklmnopqrstuvwxyz0123456789@#$';

    for (let i = 1; i <= 8; i++) {
        let char = Math.floor(Math.random()
            * str.length + 1);

        pass += str.charAt(char)
    }

    return pass;
}

export const hasAnySavedCards = (cards) => Array.isArray(cards) && cards.length > 0;

export function computeRemainingForCard(props) {
    const amountDueFull        = Number(String(props.amountDue).replace(',', '')) || 0;
    const allocatedStoreCredit = Number(props.storeCredit || 0);
    const allocatedArAmount    = Number(lodash.get(props, 'formProps.arAmount', 0));
    return Math.max(0, amountDueFull - allocatedStoreCredit - allocatedArAmount);
}

export function autoSelectNewestCardAndSetAmount(props, prevProps, { change, onCheckCard }) {
    const storeCreditAmount = Number(props.storeCredit || 0);
    const totalAmountToPay  = Number(props.totalCharge || 0);

    if (!(storeCreditAmount < totalAmountToPay)) return false;

    const rfCardsCurrent = lodash.get(props, 'card.cardsAndAchAccounts', []);
    const rfCardsPrev    = lodash.get(prevProps, 'card.cardsAndAchAccounts', []);

    const rfListGrew = rfCardsCurrent.length > rfCardsPrev.length;
    const hasRfCards = rfCardsCurrent.length > 0;

    if (!rfListGrew || !hasRfCards) return false;

    const newCardIndex = 0;

    setTimeout(() => {
        change(`card.cardsAndAchAccounts[${newCardIndex}].isChecked`, true);
        onCheckCard(`cardsAndAchAccounts[${newCardIndex}]`, { checked: true });
        const remainingForCard = computeRemainingForCard(props);
        change(`card.cardsAndAchAccounts[${newCardIndex}].amount`, remainingForCard);
    }, 0);

    return true;
}

export function shortAmount(value) {
    const intVal = Math.round(Number(value));
    const toDecimal = (x) => Math.round(x * 10) / 10;
    const stripDotZero = (s) => s.replace(/\.0$/, "");

    if (intVal >= 1000000) {
        const m = toDecimal(intVal / 1000000);

        return `${stripDotZero(m.toFixed(1))}m`;
    }

    if (intVal >= 1000) {
        const k = toDecimal(intVal / 1000);

        if (k >= 1000) return `1m`;

        return `${stripDotZero(k.toFixed(1))}k`;
    }

    return `${intVal}`;
}

export async function visitorId() {
    const norm = (v) => (v === undefined || v === null || v === '' ? 'na' : String(v)).trim().toLowerCase();
    const parts = [];

    const platformRaw = navigator.userAgentData && navigator.userAgentData.platform || navigator.platform || '';
    const platform = (() => {
        const p = norm(platformRaw);
        if (p.includes('win')) return 'windows';
        if (p.includes('mac')) return 'mac';
        if (p.includes('linux')) return 'linux';
        if (p.includes('android')) return 'android';
        if (p.includes('iphone') || p.includes('ipad') || p.includes('ios')) return 'ios';
        return 'other';
    })();
    parts.push(platform);

    const mm = (q) => (window.matchMedia ? window.matchMedia(q).matches : false);
    parts.push(mm('(pointer: coarse)') ? 'pcoarse' : 'pnotcoarse');
    parts.push(mm('(pointer: fine)') ? 'pfine' : 'pnotfine');
    parts.push(mm('(hover: hover)') ? 'hover' : 'nohover');
    parts.push(mm('(prefers-color-scheme: dark)') ? 'dark' : 'light');
    parts.push(mm('(prefers-reduced-motion: reduce)') ? 'reduce' : 'nomotionpref');

    parts.push(norm(new Date().getTimezoneOffset()));

    try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioCtx.createOscillator();
        const analyser = audioCtx.createAnalyser();
        const gain = audioCtx.createGain();
        gain.gain.value = 0;
        oscillator.connect(gain);
        gain.connect(audioCtx.destination);

        parts.push(audioCtx.sampleRate);
        audioCtx.close();
    } catch (e) {
        parts.push('no-audio');
    }

    if (navigator.mimeTypes && navigator.mimeTypes.length > 0) {
        const mimeTypes = Array.from(navigator.mimeTypes)
            .map(m => m.type)
            .sort()
            .join(',');
        parts.push(mimeTypes);
    } else {
        parts.push('no-mimes');
    }

    const raw = parts.join('|');
    const data = new TextEncoder().encode(raw);
    const digest = await crypto.subtle.digest('SHA-256', data);
    const hex = [...new Uint8Array(digest)]
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');

    return hex.slice(0, 32);
}

export default {
    Password_Regex,
    Email_Regex,
    IP_Regex,
    File_Regex,
    setSession,
    getSession,
    removeSession,
    clearSession,
    setCookie,
    getCookie,
    removeCookie,
    logMessage,
    nullCheck,
    showNotification,
    showAgentNotification,
    getInputClasses,
    phoneMask,
    AlphaNumeric_Regex,
    AlphaNumericWhiteSpace_Regex,
    getMappedDropdownOption,
    getMappedSelectBoxOption,
    Name_Regex,
    checkDate,
    deepCompare,
    currencyMask,
    yearRange,
    numberMask,
    currencyMask4,
    fourDigitMask,
    SixDigitMask,
    FiveDigitMask,
    TenDecimalMask,
    EightDecimalMask,
    TwoDecimalMask,
    sort,
    getUnitConversion,
    phoneMask12,
    characterWithHyphen,
    visaMask,
    costMask,
    expiryDateMask,
    PositiveRegex,
    NegativeRegex,
    declaredValueMask,
    NineDigitMask,
    percentMask,
    percentsMask,
    creditCardPhoneMask,
    Number_Regex,
    formattedPhoneNo,
    checkCreditCard,
    b64toBlob,
    getPhoneMask,
    convertBase64toBlob,
    numberSort,
    getBlobURL,
    getCountryPhonePrefix,
    joinWithAnd,
    formatEmptyFieldsSummary,
    getFormatIncludeCommas,
    ZIP_WAIT_INTERVAL,
    convertObjectToQueryString,
    decode_utf8,
    isShipmentFullPayed,
    isIE,
    waitUntil,
    getQueryParams,
    historyPush,
    isMobileOrTablet,
    isMobile,
    standardTimezone,
    img,
    hideNotification,
    convertWeightToPound,
    convertLengthToInch,
    isCountryWithStates,
    isCountryWithStatesFromObj,
    getStateData,
    moneyFormat,
    centsFormat,
    dollarFormat,
    NMFC_Regex,
    NMFC_Mask,
    isInvalidMonth,
    isInvalidYear,
    setGoogleAdsId,
    addGoogleAdsId,
    isEmpty,
    sendJourneyEvent,
    getQuoteItemMaxDimInInches,
    getElsFactor,
    getQuoteItemsMaxLength,
    isFloat,
    enNumberMask,
    numberMaskWhole,
    dollarMask,
    isCardExpired,
    generatePass,
    hasAnySavedCards,
    autoSelectNewestCardAndSetAmount,
    getCcProcessingFeeFinancialSettings,
    getProcessingFeeForAmount,
    swapDataBetweenQuote,
    shortAmount,
    visitorId,
    getCancellationFinancialSettings
};
